/**
 * Post-signup onboarding: server-truth helpers (subscription, TS, trading rules).
 * Used by guards, redirects, and /onboarding step resolution.
 */

import { isLocalNoCardTrialExpired } from './authAccess';

const LEGACY_INTRO_KEY = 'pt_onb_intro_seen';

function introKey(userId) {
  return userId != null ? `pt_onb_intro:${userId}` : null;
}

function rulesSkipKey(userId) {
  return userId != null ? `pt_onb_skip_rules:${userId}` : null;
}

function orientationConnectPendingKey(userId) {
  return userId != null ? `pt_orientation_connect_pending:${userId}` : null;
}

export function getIntroSeen(userId) {
  try {
    const k = introKey(userId);
    return k ? localStorage.getItem(k) === 'true' : false;
  } catch {
    return false;
  }
}

export function setIntroSeen(userId) {
  try {
    const k = introKey(userId);
    if (k) localStorage.setItem(k, 'true');
    localStorage.removeItem(LEGACY_INTRO_KEY);
  } catch {
    /* ignore */
  }
}

export function getRulesSkipped(userId) {
  try {
    const k = rulesSkipKey(userId);
    return k ? localStorage.getItem(k) === 'true' : false;
  } catch {
    return false;
  }
}

export function setRulesSkipped(userId) {
  try {
    const k = rulesSkipKey(userId);
    if (k) localStorage.setItem(k, 'true');
  } catch {
    /* ignore */
  }
}

/** True while orientation OAuth is in flight (survives the broker redirect). */
export function getOrientationConnectPending(userId) {
  try {
    const k = orientationConnectPendingKey(userId);
    return k ? sessionStorage.getItem(k) === 'true' : false;
  } catch {
    return false;
  }
}

export function setOrientationConnectPending(userId, pending) {
  try {
    const k = orientationConnectPendingKey(userId);
    if (!k) return;
    if (pending) sessionStorage.setItem(k, 'true');
    else sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

/** Clear per-user onboarding hints (call on logout with current user id). */
export function clearOnboardingLocalState(userId) {
  try {
    if (userId != null) {
      localStorage.removeItem(introKey(userId));
      localStorage.removeItem(rulesSkipKey(userId));
      sessionStorage.removeItem(orientationConnectPendingKey(userId));
    }
    localStorage.removeItem(LEGACY_INTRO_KEY);
  } catch {
    /* ignore */
  }
}

/** Active / trialing subscription (platform access). */
export function trialStarted(user) {
  if (isLocalNoCardTrialExpired(user)) return false;
  if (!user?.subscriptionStatus) return false;
  const s = user.subscriptionStatus;
  if (s.hasAccess === true) return true;
  return s.subscription?.status === 'trialing';
}

/** Read the plan tier chosen during onboarding. Returns 'pro' | 'essentials' | null. */
export function getPlanTier(user) {
  if (!user?.subscriptionStatus) return null;
  return user.subscriptionStatus.planTier || null;
}

export function hasRiskRulesConfigured(settings) {
  const rt = settings?.rule_thresholds;
  if (!rt) return false;
  const risk = rt.max_risk_per_trade;
  const loss = rt.max_loss_per_day;
  if (risk === undefined || risk === null || risk === '') return false;
  if (loss === undefined || loss === null || loss === '') return false;
  return true;
}

export function hasTradingHoursConfigured(settings) {
  const tw = settings?.rule_thresholds?.trading_windows;
  return Array.isArray(tw) && tw.length > 0;
}

function templateHasRules(template) {
  if (!template || typeof template !== 'object') return false;
  const rt = template.rule_thresholds;
  const ec = template.enforcement_config;
  return (!!rt && Object.keys(rt).length > 0) || (!!ec && Object.keys(ec).length > 0);
}

/** True when orientation (or account) already has trading-rules config to apply. */
export function hasOrientationRulesConfigured(user, accountSettings) {
  if (hasRiskRulesConfigured(accountSettings)) return true;
  return templateHasRules(user?.appSettings?.defaultTradingRulesTemplate);
}

/**
 * Live completion of the post-onboarding welcome checklist.
 * `options.brokerConnected` overrides the user flags (e.g. Connect Trade `ct=1` return).
 */
export function getOrientationSetupProgress(user, accountSettings, options = {}) {
  const brokerConnected =
    options.brokerConnected === true || !needsBrokerConnection(user);
  const rulesConfigured = hasOrientationRulesConfigured(user, accountSettings);
  return {
    brokerConnected,
    rulesConfigured,
    setupComplete: brokerConnected && rulesConfigured,
  };
}

/**
 * True when the user finished the daily-loss onboarding step but has not yet
 * seen/dismissed the post-onboarding TradeRiskSetupModal orientation.
 * Older accounts without dailyLossProtectionActivated are left alone.
 */
export function shouldShowTradeRiskOrientation(user) {
  if (!user?.token) return false;
  if (shouldBypassOnboarding(user)) return false;
  if (user.appSettings?.tradeRiskOrientationSeen === true) return false;
  if (user.appSettings?.dailyLossProtectionActivated !== true) return false;
  return true;
}

export function shouldBypassOnboarding(user) {
  if (!user?.token) return true;
  if (user.superuser === true) return true;
  if (user.is_demo === true) return true;
  return false;
}

/**
 * Expired trial / no access but already used trial → subscription paywall, not onboarding.
 * Also covers failed-payment subscriptions (past_due / unpaid) regardless of trial usage.
 */
export function needsSubscriptionPaywall(user) {
  if (!user?.token) return false;
  if (isLocalNoCardTrialExpired(user)) return true;
  const hasAccess = user.subscriptionStatus?.hasAccess === true;
  if (hasAccess) return false;
  const hasUsedTrial = user.subscriptionStatus?.hasUsedTrial === true;
  if (hasUsedTrial) return true;
  // Payment failed on a direct subscription (no trial): still needs paywall, not onboarding
  const subStatus = user.subscriptionStatus?.subscription?.status;
  if (subStatus === 'past_due' || subStatus === 'unpaid' || subStatus === 'trial_expired') return true;
  return false;
}

/**
 * True when the user's most recent subscription has a failed payment.
 * These users need to update their payment method, not start a new subscription.
 */
export function isPaymentFailed(user) {
  if (!user?.token) return false;
  const status = user.subscriptionStatus?.subscription?.status;
  return status === 'past_due' || status === 'unpaid';
}

/**
 * Core funnel: subscription (trial) only. Broker connection is intentionally NOT required to
 * clear onboarding — new users land straight in the journal/watchlist/Essentials-style experience
 * and are prompted to connect a broker later (same "disconnected" UX already used on /trade).
 * First-rule setup is optional on /onboarding (skip) and does not block platform access.
 * @param {object|null} _settings - unused; kept for call-site compatibility
 */
export function isOnboardingIncomplete(user, _settings) {
  if (shouldBypassOnboarding(user)) return false;
  if (needsSubscriptionPaywall(user)) return false;
  if (!trialStarted(user)) return true;
  // Profile step (name + asset class + phone verify) must be completed before platform access —
  // otherwise OAuth signups (which land on /trade by default) or direct "/" visits can skip
  // straight past it. See Onboarding.js "profile-phone" step.
  //
  // Existing accounts were grandfathered with phone_verification_skipped=TRUE
  // (migration 1906000000000_*) so releasing mandatory phone verify does not force them back
  // into onboarding. Only new signups (neither verified nor skipped) are blocked here.
  if (!user?.phoneVerified && !user?.phoneVerificationSkipped) return true;
  return false;
}

/** True when the user has not yet linked a broker — used to show "connect later" prompts, not to gate access. */
export function needsBrokerConnection(user) {
  return !user?.hasBrokerConnection && !user?.hasTradeStationCredentials;
}

export function shouldRedirectToOnboarding(user, settings) {
  return isOnboardingIncomplete(user, settings);
}

/** Merge partial user fields (e.g. after refreshUserSettings response) for redirect checks. */
export function mergeUserSnapshot(user, partial) {
  if (!user?.token) return user;
  if (!partial) return user;
  return { ...user, ...partial, token: user.token };
}
